/**
 * Mediasoup Manager (factory function)
 *
 * Handles WebRTC transport, producer, and consumer management
 * using the mediasoup-client library.
 */

import * as mediasoupClient from "mediasoup-client";
import {Device, types} from "mediasoup-client";
import {Socket} from "socket.io-client";
import {createEventBus, EventBus} from "./EventManager";
import {OdysseyEvent} from "../types/events";

// ─── Public types ─────────────────────────────────────────────────────────────

export type TransportEvent =
  | "transport-connected"
  | "transport-disconnected"
  | "transport-failed"
  | "transport-closed"
  | "transport-connecting";

export interface ConsumeData {
  consumerId: string;
  producerId: string;
  kind: "audio" | "video";
  rtpParameters: types.RtpParameters;
  participantId: string;
}

// ─── Factory handle interface ──────────────────────────────────────────────────

export interface MediasoupManagerHandle {
  loadDevice(routerRtpCapabilities: types.RtpCapabilities): Promise<void>;
  sendDeviceRtpCapabilities(participantId: string): void;
  createSendTransport(participantId: string): Promise<void>;
  createRecvTransport(participantId: string): Promise<void>;
  produce(
    track: MediaStreamTrack,
    appData?: {isScreenshare?: boolean}
  ): Promise<types.Producer>;
  consume(
    data: ConsumeData
  ): Promise<{consumer: types.Consumer; track: MediaStreamTrack}>;
  resumeConsumer(consumerId: string): Promise<void>;
  getTransportStates(): {send: string; recv: string};
  getConsumers(): Map<string, types.Consumer>;
  close(): void;
  onTransportEvent(
    event: TransportEvent,
    listener: (data: {direction: string; reason?: string}) => void
  ): void;
}

// ─── Factory function (preferred) ────────────────────────────────────────────

export function createMediasoupManager(socket: Socket): MediasoupManagerHandle {
  const device = new Device();
  let sendTransport: types.Transport | null = null;
  let recvTransport: types.Transport | null = null;
  const producers = new Map<string, types.Producer>();
  const consumers = new Map<string, types.Consumer>();
  let participantId = "";
  let sendTransportState = "new";
  let recvTransportState = "new";
  let iceRestartAttempts = 0;
  const MAX_ICE_RESTART_ATTEMPTS = 3;

  // Internal event bus for transport events (replaces EventEmitter inheritance)
  const internalBus: EventBus = createEventBus();

  function emitTransportEvent(
    event: TransportEvent,
    data: {direction: string; reason?: string}
  ): void {
    internalBus.emit(event as OdysseyEvent, data);
  }

  async function loadDevice(
    routerRtpCapabilities: types.RtpCapabilities
  ): Promise<void> {
    if (device.loaded) return;
    await device.load({routerRtpCapabilities});
  }

  function sendDeviceRtpCapabilities(pid: string): void {
    participantId = pid;
    socket.emit("device-rtp-capabilities", {
      participantId: pid,
      rtpCapabilities: device.rtpCapabilities,
    });
  }

  function createWebRtcTransport(
    direction: "send" | "recv",
    pid: string
  ): Promise<mediasoupClient.types.TransportOptions> {
    return new Promise((resolve, reject) => {
      const handleTransportCreated = (data: {
        type: string;
        error?: string;
        params: mediasoupClient.types.TransportOptions;
      }) => {
        if (data.type === direction) {
          socket.off("transport-created", handleTransportCreated);
          if (data.error) return reject(new Error(data.error));
          resolve(data.params);
        }
      };

      socket.on("transport-created", handleTransportCreated);
      socket.emit("create-transport", {direction, participantId: pid});

      setTimeout(() => {
        socket.off("transport-created", handleTransportCreated);
        reject(new Error(`Timeout waiting for ${direction} transport`));
      }, 10000);
    });
  }

  function attemptIceRestart(direction: "send" | "recv"): void {
    if (iceRestartAttempts >= MAX_ICE_RESTART_ATTEMPTS) {
      emitTransportEvent("transport-failed", {
        direction,
        reason: "max-ice-restarts",
      });
      return;
    }

    iceRestartAttempts++;
    const transport = direction === "send" ? sendTransport : recvTransport;
    if (!transport) return;

    socket.emit(
      "request-ice-restart",
      {participantId, transportId: transport.id, direction},
      async (response: {
        error?: string;
        iceParameters?: types.IceParameters;
      }) => {
        if (response.error) {
          setTimeout(() => attemptIceRestart(direction), 2000);
          return;
        }
        if (response.iceParameters) {
          try {
            await transport.restartIce({iceParameters: response.iceParameters});
          } catch {
            setTimeout(() => attemptIceRestart(direction), 2000);
          }
        }
      }
    );
  }

  function connectSendTransport(): void {
    sendTransport?.on(
      "connect",
      async (
        {dtlsParameters}: {dtlsParameters: types.DtlsParameters},
        callback: () => void,
        errback: (error: Error) => void
      ) => {
        socket.emit(
          "connect-transport",
          {transportId: sendTransport!.id, dtlsParameters},
          (response: {error?: string}) => {
            if (response.error) {
              errback(new Error(response.error));
            } else {
              callback();
            }
          }
        );
      }
    );

    sendTransport?.on(
      "produce",
      async (
        {
          kind,
          rtpParameters,
          appData,
        }: {
          kind: types.MediaKind;
          rtpParameters: types.RtpParameters;
          appData: Record<string, unknown>;
        },
        callback: ({id}: {id: string}) => void,
        errback: (error: Error) => void
      ) => {
        socket.emit(
          "produce",
          {
            transportId: sendTransport!.id,
            kind,
            rtpParameters,
            appData,
          },
          (response: {error?: string; producerId?: string}) => {
            if (response.error) {
              errback(new Error(response.error));
            } else if (response.producerId) {
              callback({id: response.producerId});
            }
          }
        );
      }
    );

    sendTransport?.on("connectionstatechange", (state: string) => {
      sendTransportState = state;
      switch (state) {
        case "connected":
          iceRestartAttempts = 0;
          emitTransportEvent("transport-connected", {direction: "send"});
          break;
        case "disconnected":
          emitTransportEvent("transport-disconnected", {direction: "send"});
          attemptIceRestart("send");
          break;
        case "failed":
          producers.clear();
          emitTransportEvent("transport-failed", {direction: "send"});
          socket.emit("transport-failed", {participantId, direction: "send"});
          break;
        case "closed":
          producers.clear();
          emitTransportEvent("transport-closed", {direction: "send"});
          break;
        case "connecting":
          emitTransportEvent("transport-connecting", {direction: "send"});
          break;
      }
    });
  }

  function connectRecvTransport(): void {
    recvTransport?.on(
      "connect",
      async (
        {dtlsParameters}: {dtlsParameters: types.DtlsParameters},
        callback: () => void,
        errback: (error: Error) => void
      ) => {
        socket.emit(
          "connect-transport",
          {transportId: recvTransport!.id, dtlsParameters},
          (response: {error?: string}) => {
            if (response.error) {
              errback(new Error(response.error));
            } else {
              callback();
            }
          }
        );
      }
    );

    recvTransport?.on("connectionstatechange", (state: string) => {
      recvTransportState = state;
      switch (state) {
        case "connected":
          iceRestartAttempts = 0;
          emitTransportEvent("transport-connected", {direction: "recv"});
          break;
        case "disconnected":
          emitTransportEvent("transport-disconnected", {direction: "recv"});
          attemptIceRestart("recv");
          break;
        case "failed":
          emitTransportEvent("transport-failed", {direction: "recv"});
          socket.emit("transport-failed", {participantId, direction: "recv"});
          break;
        case "closed":
          emitTransportEvent("transport-closed", {direction: "recv"});
          break;
        case "connecting":
          emitTransportEvent("transport-connecting", {direction: "recv"});
          break;
      }
    });
  }

  async function createSendTransport(pid: string): Promise<void> {
    const params = await createWebRtcTransport("send", pid);
    const {iceServers, ...transportParams} = params as typeof params & {
      iceServers?: RTCIceServer[];
    };
    sendTransport = device.createSendTransport({
      ...transportParams,
      iceServers: iceServers || [],
    });
    connectSendTransport();
  }

  async function createRecvTransport(pid: string): Promise<void> {
    const params = await createWebRtcTransport("recv", pid);
    const {iceServers, ...transportParams} = params as typeof params & {
      iceServers?: RTCIceServer[];
    };
    recvTransport = device.createRecvTransport({
      ...transportParams,
      iceServers: iceServers || [],
    });
    connectRecvTransport();
  }

  async function produce(
    track: MediaStreamTrack,
    appData?: {isScreenshare?: boolean}
  ): Promise<types.Producer> {
    if (!sendTransport) throw new Error("Send transport not initialized");

    const produceOptions: {
      track: MediaStreamTrack;
      appData?: Record<string, unknown>;
      encodings?: types.RtpEncodingParameters[];
      codecOptions?: {videoGoogleStartBitrate?: number};
    } = {track, appData};

    const isScreenshare = appData?.isScreenshare === true;

    if (track.kind === "video") {
      if (isScreenshare) {
        produceOptions.encodings = [
          {
            rid: "r0",
            active: true,
            maxBitrate: 500000,
            maxFramerate: 10,
            scaleResolutionDownBy: 4,
          },
          {
            rid: "r1",
            active: true,
            maxBitrate: 1500000,
            maxFramerate: 15,
            scaleResolutionDownBy: 2,
          },
          {
            rid: "r2",
            active: true,
            maxBitrate: 3000000,
            maxFramerate: 15,
            scaleResolutionDownBy: 1,
          },
        ];
        produceOptions.codecOptions = {videoGoogleStartBitrate: 1500};
      } else {
        produceOptions.encodings = [
          {
            rid: "r0",
            active: true,
            maxBitrate: 100000,
            scaleResolutionDownBy: 4,
          },
          {
            rid: "r1",
            active: true,
            maxBitrate: 300000,
            scaleResolutionDownBy: 2,
          },
          {
            rid: "r2",
            active: true,
            maxBitrate: 900000,
            scaleResolutionDownBy: 1,
          },
        ];
        produceOptions.codecOptions = {videoGoogleStartBitrate: 1000};
      }
    }

    const producer = await sendTransport.produce(produceOptions);

    producer.on("transportclose", () => {
      producers.delete(producer.id);
    });
    producer.on("trackended", () => {
      producer.close();
      producers.delete(producer.id);
    });

    producers.set(producer.id, producer);
    return producer;
  }

  async function consume(
    data: ConsumeData
  ): Promise<{consumer: types.Consumer; track: MediaStreamTrack}> {
    if (!recvTransport) throw new Error("Receive transport not set up");

    const consumer = await recvTransport.consume({
      id: data.consumerId,
      producerId: data.producerId,
      kind: data.kind,
      rtpParameters: data.rtpParameters,
    });

    consumer.on("transportclose", () => {
      consumers.delete(consumer.id);
    });
    consumer.on("trackended", () => {
      consumer.close();
      consumers.delete(consumer.id);
    });

    consumers.set(consumer.id, consumer);
    return {consumer, track: consumer.track};
  }

  async function resumeConsumer(consumerId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      socket.emit(
        "resume-consumer",
        {consumerId, participantId},
        (response: {error?: string}) => {
          if (response.error) {
            reject(new Error(response.error));
          } else {
            resolve();
          }
        }
      );
    });
  }

  function getTransportStates(): {send: string; recv: string} {
    return {send: sendTransportState, recv: recvTransportState};
  }

  function getConsumers(): Map<string, types.Consumer> {
    return consumers;
  }

  function close(): void {
    producers.forEach((p) => p.close());
    consumers.forEach((c) => c.close());
    if (sendTransport) sendTransport.close();
    if (recvTransport) recvTransport.close();
    producers.clear();
    consumers.clear();
  }

  return {
    loadDevice,
    sendDeviceRtpCapabilities,
    createSendTransport,
    createRecvTransport,
    produce,
    consume,
    resumeConsumer,
    getTransportStates,
    getConsumers,
    close,
    onTransportEvent: (
      event: TransportEvent,
      listener: (data: {direction: string; reason?: string}) => void
    ) => internalBus.on(event as OdysseyEvent, listener),
  };
}
